import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Pencil, Save, X, RotateCcw, Eye } from 'lucide-react';
import * as api from '../../utils/channelsApi';
import { useDraftSaver } from '../../contexts/IdleContext';
import { draftStore, DRAFT_SCOPE, getDraftUserId } from '../../idle-session';
import { InsertVariableButton, styles } from './templateUI';

// Platform wording for the browser completion card. Unlike a chat thank-you,
// this one cannot be silent: an empty card is a broken page, so clearing the
// message restores this text rather than sending nothing.
export const BROWSER_COMPLETION_DEFAULT = 'Thank you — your responses have been recorded.';
const SENDER_NAME = { token: '{{sender_name}}', description: "The account owner's name — recipients recognize a person, not a product", sample: 'Jason Cavey' };
const SURVEY_NAME = { token: '{{survey_name}}', description: 'The survey title', sample: 'Employee Engagement 2026' };
const RECIPIENT_NAME = { token: '{{recipient_name}}', description: "The recipient's name (falls back to their email)", sample: 'Jane Doe' };
const ORG_NAME = { token: '{{org_name}}', description: "Your organization's name", sample: 'Your Organization' };

export const MESSAGE_TYPES = [
    {
        value: 'invitation',
        label: 'Invitation',
        sentHint: 'Sent as the first message of a new conversation.',
        emptyHint: 'Using the platform wording.',
        clearLabel: 'Restore platform wording',
        clearTitle: 'Empty this message so the platform wording goes out instead',
        placeholder: "Hello from {{sender_name}} via Sam Surveyor. You're invited to take {{survey_name}}. Answer right here in this chat.",
        fallsBackToPlatform: true,
        variables: [SENDER_NAME, SURVEY_NAME, RECIPIENT_NAME, ORG_NAME],
    },
    {
        value: 'completion',
        label: 'Thank-you',
        sentHint: 'Sent into the same conversation when the survey completes.',
        emptyHint: 'Nothing is sent.',
        clearLabel: 'Clear',
        clearTitle: 'Empty this message so nothing extra is sent',
        placeholder: 'Nothing is sent unless you write something here.',
        fallsBackToPlatform: false,
        variables: [SENDER_NAME, SURVEY_NAME, ORG_NAME],
    },
];

// The browser is a channel like any other, and its end-of-survey copy belongs
// on this screen beside the chat sign-offs. It takes a completion message only:
// there is no browser invitation, because the invitation is what opened the
// page. Storage needed nothing new — channel_message_templates has had the
// channel in its primary key since migration 072 (089 widens the CHECK).
const BROWSER_MESSAGE_TYPES = [
    {
        value: 'completion',
        label: 'Completion card',
        sentHint: 'Shown on screen when a respondent finishes a survey in a browser.',
        emptyHint: 'Using the platform wording.',
        clearLabel: 'Restore platform wording',
        clearTitle: 'Empty this message so the platform wording appears instead',
        placeholder: BROWSER_COMPLETION_DEFAULT,
        fallsBackToPlatform: true,
        // No {{recipient_name}}: the card's words are prepared when the survey
        // opens, and the respondent's name is not read at that point. Offering a
        // token that would render as a blank would be worse than not offering it.
        variables: [SURVEY_NAME, ORG_NAME],
    },
];

const CHANNELS = [
    { value: 'googlechat', label: 'Google Chat', shape: 'chat', types: MESSAGE_TYPES },
    { value: 'browser', label: 'Browser', shape: 'card', types: BROWSER_MESSAGE_TYPES },
];

/** Every authored message on this screen, flattened to one addressable list. */
export const SLOTS = CHANNELS.flatMap(channel => channel.types.map(type => ({
    ...type,
    key: `${channel.value}:${type.value}`,
    channel: channel.value,
    channelLabel: channel.label,
    shape: channel.shape,
})));

const typeSpec = key => SLOTS.find(slot => slot.key === key) || SLOTS[0];

export function renderChatPreview(bodyText, spec, facts) {
    const allowed = new Set(spec.variables.map(variable => variable.token));
    const pairs = {
        '{{sender_name}}': facts.senderName,
        '{{survey_name}}': facts.surveyName,
        '{{recipient_name}}': facts.recipientName,
        '{{org_name}}': facts.orgName,
    };
    return Object.entries(pairs)
        .filter(([token]) => allowed.has(token))
        .reduce((result, [token, value]) => result.split(token).join(value), bodyText || '')
        .trim();
}

function ChatPreview({ bodyText, spec, org }) {
    const facts = {
        senderName: SENDER_NAME.sample,
        surveyName: SURVEY_NAME.sample,
        recipientName: RECIPIENT_NAME.sample,
        orgName: org?.formal_name || org?.name || ORG_NAME.sample,
    };
    const effective = bodyText.trim() ? bodyText : (spec.fallsBackToPlatform ? spec.placeholder : '');
    const rendered = renderChatPreview(effective, spec, facts);
    const isCard = spec.shape === 'card';
    return (
        <div style={styles.preview}>
            <div style={styles.previewHeader}>
                <Eye size={13} style={{ marginRight: 6 }} />
                {isCard ? 'Preview — how this card appears' : 'Preview — how this message arrives'}
            </div>
            {rendered
                ? (isCard ? <ScrimmedCardPreview text={rendered} /> : <div style={styles.chatBubble}>{rendered}</div>)
                : <div style={styles.chatBubbleEmpty}>Nothing is sent.</div>}
            {!bodyText.trim() && spec.fallsBackToPlatform && <div style={styles.hint}>This is the platform wording. Type your own above to replace it.</div>}
        </div>
    );
}

/**
 * A miniature of the real thing: the conversation dimmed behind a scrim, with
 * the completion card over it. Authors were writing this copy blind otherwise —
 * a chat bubble is the wrong mental picture for a full-page ending.
 */
function ScrimmedCardPreview({ text }) {
    return (
        <div style={{ position: 'relative', borderRadius: 8, overflow: 'hidden', background: '#0F172A', padding: 20, minHeight: 116 }}>
            <div aria-hidden="true" style={{ opacity: 0.28, filter: 'blur(1px)', fontSize: 12, lineHeight: 1.8, color: '#E2E8F0' }}>
                <div>Sam: And finally, how likely are you to recommend us?</div>
                <div style={{ textAlign: 'right' }}>Very likely.</div>
            </div>
            <div style={{
                position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center',
                background: 'rgba(15,23,42,0.72)', padding: 16,
            }}>
                <div style={{
                    background: '#FFFFFF', color: '#1A1A1A', borderRadius: 10, padding: '16px 20px',
                    maxWidth: 320, textAlign: 'center', fontSize: 13, lineHeight: 1.5,
                    boxShadow: '0 10px 30px rgba(0,0,0,0.35)',
                }}>
                    <div style={{ color: '#2E7D32', fontSize: 20, lineHeight: 1, marginBottom: 8 }}>✓</div>
                    {text}
                    <div style={{ marginTop: 10, fontSize: 11, color: '#5F6368' }}>You may now close this tab.</div>
                </div>
            </div>
        </div>
    );
}

export function AccountChannelMessagesSection({ orgId, org, canPersist = true, renderToggle, onNotice }) {
    const [bodies, setBodies] = useState({});
    const [loading, setLoading] = useState(false);
    const [editing, setEditing] = useState(null);
    const [draft, setDraft] = useState('');
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState('');
    const [notice, setNotice] = useState('');
    const bodyRef = useRef(null);

    const load = useCallback(async () => {
        if (!orgId) return;
        setLoading(true);
        setError('');
        try {
            // One request per channel, and each channel's rows are filed under
            // that channel's slots. Reading the channel back off the row would
            // work too, but this way a row that arrives without one cannot end
            // up filed against the wrong card.
            const perChannel = await Promise.all(
                CHANNELS.map(channel => api.listChannelMessages(orgId, channel.value)
                    .then(rows => [channel.value, rows])),
            );
            const next = {};
            SLOTS.forEach(slot => { next[slot.key] = ''; });
            perChannel.forEach(([channelValue, rows]) => {
                (rows || []).forEach(row => {
                    const key = `${channelValue}:${row.type}`;
                    if (key in next) next[key] = row.body_text || '';
                });
            });
            setBodies(next);
        } catch (loadError) {
            if (loadError.code !== api.NOT_SIGNED_IN) setError(loadError.message);
        } finally {
            setLoading(false);
        }
    }, [orgId]);

    useEffect(() => { load(); }, [load]);

    // ── Rescuing typed message copy before an idle sign-out ────────────────
    //
    // Unlike the Bindings panel, nothing here writes to the server until Save
    // is pressed. So a message somebody was part way through typing exists only
    // in this component. Without this, thirty idle minutes throws it away.
    //
    // The rescue is local storage only. Pushing half-typed copy to the server
    // would publish wording nobody approved, and the next invitation would go
    // out carrying it.
    const userId = getDraftUserId();
    const draftKeyFor = useCallback(
        (slotKey) => `message|${orgId || 'no-org'}|${slotKey}`,
        [orgId],
    );

    const [recovered, setRecovered] = useState(null);
    const recoveryReadRef = useRef(false);

    useEffect(() => {
        if (!userId || !orgId || recoveryReadRef.current) return;
        recoveryReadRef.current = true;
        for (const slot of SLOTS) {
            const found = draftStore.readDraft({ userId, scope: DRAFT_SCOPE, key: draftKeyFor(slot.key) });
            if (found && typeof found.payload === 'string') {
                setRecovered({ type: slot.key, text: found.payload, savedAt: found.savedAt });
                break;
            }
        }
    }, [userId, orgId, draftKeyFor]);

    useDraftSaver(() => {
        // Nothing open, or nothing changed, is not the same as a failed save.
        if (!editing || !userId) return undefined;
        const current = draft;
        if (current === (bodies[editing] || '')) return undefined;
        const spec = typeSpec(editing);
        const ok = draftStore.saveDraft({
            userId,
            scope: DRAFT_SCOPE,
            key: draftKeyFor(editing),
            payload: current,
            label: `${spec.label} message`,
        });
        return { saved: ok, where: 'local', label: `${spec.label} message` };
    }, [editing, draft, bodies, userId, draftKeyFor]);

    /** Take the rescued copy back into the editor. Always the author's call. */
    const restoreRecovered = () => {
        if (!recovered) return;
        setEditing(recovered.type);
        setDraft(recovered.text);
        setError('');
        setNotice('');
        draftStore.clearDraft({ userId, scope: DRAFT_SCOPE, key: draftKeyFor(recovered.type) });
        setRecovered(null);
    };

    const discardRecovered = () => {
        if (!recovered) return;
        draftStore.clearDraft({ userId, scope: DRAFT_SCOPE, key: draftKeyFor(recovered.type) });
        setRecovered(null);
    };

    const handleEdit = (type) => {
        setEditing(type);
        setDraft(bodies[type] || '');
        setError('');
        setNotice('');
    };

    const handleSave = async () => {
        setSaving(true);
        setError('');
        try {
            const slot = typeSpec(editing);
            await api.saveChannelMessage({ orgId, channel: slot.channel, type: slot.value, bodyText: draft });
            // A real save means the rescued copy has done its job. Leaving it
            // would offer this wording back every time the panel opened.
            if (userId) draftStore.clearDraft({ userId, scope: DRAFT_SCOPE, key: draftKeyFor(editing) });
            const spec = typeSpec(editing);
            setNotice(draft.trim() ? `${spec.label} saved.` : `${spec.label} cleared — ${spec.emptyHint.toLowerCase()}`);
            setEditing(null);
            await load();
            onNotice?.('Channel message saved');
        } catch (saveError) {
            setError(saveError.message);
        } finally {
            setSaving(false);
        }
    };

    const spec = editing ? typeSpec(editing) : null;
    const dirty = editing ? draft !== (bodies[editing] || '') : false;

    return (
        <div>
            <div style={styles.commandRow}>{renderToggle ? renderToggle(Boolean(editing) && dirty) : <span />}</div>
            {/* Never restored on its own. Somebody who deliberately abandoned a
                message would otherwise be handed it straight back with no idea
                where it came from. */}
            {recovered && !editing && (
                <div className="draft-recovery" role="status">
                    <div className="draft-recovery__text">
                        <strong className="draft-recovery__title">
                            Unsaved wording from your last session was kept.
                        </strong>
                        <span className="draft-recovery__body">
                            You were part way through the {typeSpec(recovered.type).channelLabel} {typeSpec(recovered.type).label.toLowerCase()}
                            when you were signed out. It was never sent to anybody.
                        </span>
                    </div>
                    <div className="draft-recovery__actions">
                        <button type="button" className="draft-recovery__restore" onClick={restoreRecovered}>
                            Restore it
                        </button>
                        <button type="button" className="draft-recovery__discard" onClick={discardRecovered}>
                            Discard it
                        </button>
                    </div>
                </div>
            )}
            {error && <div style={styles.error}>{error}</div>}
            {notice && !editing && <div style={styles.notice}>{notice}</div>}

            {!editing ? (
                loading ? <p style={{ color: '#94A3B8', fontSize: 14 }}>Loading…</p> : (
                    <div style={styles.list}>
                        {SLOTS.map(slot => {
                            const body = bodies[slot.key] || '';
                            return (
                                <div key={slot.key} style={styles.card}>
                                    <div style={{ flex: 1, minWidth: 0 }}>
                                        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
                                            <span style={styles.typeBadge}>{slot.channelLabel}</span>
                                            <span style={styles.typeBadge}>{slot.label}</span>
                                            <strong style={styles.cardTitle}>{slot.sentHint}</strong>
                                        </div>
                                        <p style={body ? styles.cardBody : styles.cardBodyUnset}>{body ? body.substring(0, 120) : slot.emptyHint}</p>
                                    </div>
                                    <div style={styles.cardActions}>
                                        <button type="button" onClick={() => handleEdit(slot.key)} style={styles.iconBtn} title={`Edit the ${slot.channelLabel} ${slot.label.toLowerCase()}`} aria-label={`Edit the ${slot.channelLabel} ${slot.label.toLowerCase()}`}><Pencil size={14} /></button>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                )
            ) : (
                <div style={styles.formCard}>
                    <h3 style={{ ...styles.formTitle, marginBottom: 4 }}>Edit the {spec.channelLabel} {spec.label.toLowerCase()}</h3>
                    <p style={{ ...styles.subtitle, margin: '0 0 20px' }}>{spec.sentHint}</p>
                    <div style={styles.fieldGroup}>
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 6 }}>
                            <label style={{ ...styles.label, marginBottom: 0 }}>Message (plain text)</label>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                <button type="button" onClick={() => setDraft('')} disabled={!draft} style={{ ...styles.resetBtn, ...(!draft ? styles.btnDisabled : {}) }} title={spec.clearTitle}><RotateCcw size={12} style={{ marginRight: 4 }} /> {spec.clearLabel}</button>
                                <InsertVariableButton targetRef={bodyRef} formField="body" setForm={updater => setDraft(previous => updater({ body: previous }).body)} variables={spec.variables} />
                            </div>
                        </div>
                        <textarea ref={bodyRef} value={draft} onChange={event => setDraft(event.target.value)} rows={4} placeholder={spec.placeholder} style={{ ...styles.input, resize: 'vertical', fontFamily: 'monospace', fontSize: 13 }} />
                        <span style={styles.hint}>{spec.shape === 'card'
                            ? 'Plain text only. Keep it short — it sits in a small card over the finished survey.'
                            : 'Plain text only — no subject, no formatting, no link.'}</span>
                    </div>
                    <ChatPreview bodyText={draft} spec={spec} org={org} />
                    <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
                        <button type="button" onClick={handleSave} disabled={saving || !dirty || !canPersist} title={!canPersist ? 'A signed-in Console session is required to save this draft.' : undefined} style={{ ...styles.saveBtn, ...(saving || !dirty || !canPersist ? styles.btnDisabled : {}) }}><Save size={14} style={{ marginRight: 6 }} /> {saving ? 'Saving…' : 'Save'}</button>
                        <button type="button" onClick={() => setEditing(null)} style={styles.cancelBtn}><X size={14} style={{ marginRight: 6 }} /> Cancel</button>
                    </div>
                </div>
            )}
        </div>
    );
}
